import {
	Box,
	Card,
	Grid,
	Menu,
	MenuItem,
	Stack,
	Typography,
} from '@mui/material';

import { useEffect, useState, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import { v4 as uuidv4 } from 'uuid';

import { flushSync } from 'react-dom';

import { IWorkFlowFeedback } from 'common/api/type-def';
import { comingSoon } from 'common/assets/images';
import { ReactComponent as BackIcon } from 'common/assets/images/button-previous.svg';
import { ReactComponent as DownArrowDropdown } from 'common/assets/images/down-arrow-dropdown.svg';
import AIFeedback from 'common/components/AIFeedback/AIFeedback';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import { MODULES } from 'common/constants';
import {
	AI_SIMULATION_GENAI_DISCLAIMER,
	AI_SIMULATION_MODEL_KEYS,
	AI_SIMULATION_TYPES,
	AI_TYPE,
	LOCATION_OPTIONS,
	OBLIGATION_STOP_GENERATING,
	OBLIGATION_STOPPING,
} from 'common/constants/AISimulationConstants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	APP_GENERAL_ERROR_MSG,
	APP_GENERIC_BAD_REQUEST_MSG,
	APP_GENERIC_DATA_NOT_FOUND_MSG,
} from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setObligationToPolicyCommentList,
	setObligtionToPolicyRegenerate,
	setSimulationCommonSaveDrawer,
	setSimulationCommonShareDrawer,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import {
	setAIRecipeDetail,
	setWorkflowFeedback,
} from 'common/store/reducer/simulationReducer';
import { scrollProps, toSentenceCase } from 'common/utils/utilFunctions';

import { useFeatureFlag } from 'hooks/useFeatureFlags';

import {
	mapTitleWithModel,
	navigateToRegulationControl,
} from '../AiSimulationUtil';
import {
	deleteObligationComments,
	generateAISimulationPromptResult,
	generateAISimulationResult,
	genPromptRegAnalysis,
	genRegAnalysis,
	getObligationComments,
	postObligationComments,
} from '../api';
import {
	IAIRecipeDetails,
	IObligationToPolicyInput,
	IObligationToPolicyOutput,
	IObligationComments,
	IObligationToPolicyComment,
	ResultAnalysis,
} from '../api/type-def';
import AIRecipeSave from '../Components/AIRecipeSave/AIRecipeSave';
import AIShare from '../Components/AIShare/AIShare';
import StepHeader from '../Components/StepHeader/StepHeader';

import ObligationPolicyInput from './ObligationPolicyInput';
import ResultCard from './ResultCard';
import { generateResultExcel, generateResultPDF } from './ResultCardDownload';

import styles from './ObligationToPolicy.module.scss';

const { primaryBlue, downloadBtnBgColor, downloadBtnTextColor } = styles;

const ObligationToPolicy = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { RECIPE } = AI_TYPE;
	const { OK, BAD_REQUEST, NOT_FOUND } = HTTP_STATUS_CODES;
	const { IS_EXPORT_BUTTON_ENABLED } = FeatureFlagsKeysEnum;

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isLoadingResult, setIsLoadingResult] = useState<boolean>(false);
	const [loadNextResult, setLoadNextResult] = useState<boolean>(true);
	const loadNextResultRef = useRef(loadNextResult);
	const [inputType, setInputType] = useState<'file' | 'text' | null>(null);
	const [citationInput, setCitationInput] = useState<string>('');
	const [obligation, setObligation] = useState<string>('');
	const [analysisResult, setAnalysisResult] = useState<
		IObligationToPolicyOutput[]
	>([]);
	const [enableBtn, setEnableBtn] = useState<boolean>(false);
	const [selectedDocument, setSelectedDocument] = useState<string[]>([]);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const { REQ_INV } = LOCATION_OPTIONS;

	const open = Boolean(anchorEl);
	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(null);
	};
	const selectedAIRecipe = useSelector(
		(state: RootState) => state.simulationArena.selectedAIRecipe,
	);

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const obligationToPolicyCommentList = useSelector(
		(state: RootState) => state.common.obligationToPolicyCommentList,
	);
	const obligtionToPolicyRegenerate = useSelector(
		(state: RootState) => state.common.obligtionToPolicyRegenerate,
	);

	const regulationText = useSelector(
		(state: RootState) => state.simulationArena.regulationText,
	);

	const selectedRecipe = useSelector(
		(state: RootState) => state.simulationArena.selectedRecipe,
	);

	const workflowFeedback = useSelector(
		(state: RootState) => state.simulationArena.workflowFeedback,
	);
	const isLDResponseExists: boolean = useSelector(
		(state: RootState) => state.common.isLDResponseAvailable,
	);

	const { OBLIGATION_TO_POLICY } = AI_SIMULATION_TYPES;
	const { OBLIGATION_GENERATION_KEY } = AI_SIMULATION_MODEL_KEYS;
	const { DISCLAIMER } = AI_SIMULATION_GENAI_DISCLAIMER;
	const { AI_SIMULATION_AND_EXPERIMENTATION } = MODULES;
	const isExportButtonEnabled = useFeatureFlag(
		IS_EXPORT_BUTTON_ENABLED,
		isLDResponseExists,
	);

	const aiRecipeData: IAIRecipeDetails = {
		...(selectedAIRecipe?.id ? { id: selectedAIRecipe.id } : {}),
		name: selectedAIRecipe?.name || '',
		models: [
			{
				input: {
					citationInput,
					obligation,
					selectedDocument,
				} as IObligationToPolicyInput,
				output: analysisResult,
				workflowStep: OBLIGATION_TO_POLICY,
			},
		],
		type: RECIPE,
	};

	useEffect(() => {
		dispatch(setObligationToPolicyCommentList([]));
		dispatch(setObligtionToPolicyRegenerate(false));
	}, []);

	useEffect(() => {
		loadNextResultRef.current = loadNextResult;
	}, [loadNextResult]);

	useEffect(() => {
		if (selectedAIRecipe.id) {
			setEnableBtn(true);
			const input = selectedAIRecipe.models[0]
				.input as IObligationToPolicyInput;
			const output = selectedAIRecipe.models[0]
				.output as IObligationToPolicyOutput[];
			setInputType('text');
			setCitationInput(input.citationInput);
			setObligation(input.obligation);
			setSelectedDocument(input.selectedDocument);
			setAnalysisResult(output);
			getObligationToPolicyComments(selectedAIRecipe.id);
		} else {
			setInputType('text');
			setCitationInput(regulationText);
			setObligation('');
			setSelectedDocument([]);
		}
		return () => {
			setEnableBtn(false);
		};
	}, [selectedAIRecipe]);

	const handlePostObligationComments = async (postData: any) => {
		try {
			const resp = await postObligationComments(postData);
			if (resp && resp.responseBody && selectedAIRecipe.id) {
				getObligationToPolicyComments(selectedAIRecipe.id);
			}
		} catch (error) {
			dispatch(
				setToastMessage({
					message: 'Error fetching obligation comments',
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const saveSimulationComment = (
		simulationName: string,
		simulationId: string,
		canDeleteComment: boolean,
	) => {
		const saveCommentList: any = [];
		obligationToPolicyCommentList.forEach((ele) => {
			if (!ele.simulationDetailId) {
				saveCommentList.push({
					comment: ele.comment,
					paragraphId: ele.paragraphId,
				});
			}
		});
		if (canDeleteComment) {
			deleteButtonClicked(selectedAIRecipe.id);
		}
		if (obligtionToPolicyRegenerate && canDeleteComment !== true) {
			deleteButtonClicked(selectedAIRecipe.id);
		}

		const postData: IObligationToPolicyComment = {
			simulationName,
			simulationId,
			type: RECIPE,
			comments: saveCommentList,
		};
		if (saveCommentList.length > 0) {
			handlePostObligationComments(postData);
		}
	};

	const handleRestart = () => {
		dispatch(setWorkflowFeedback([]));
		dispatch(setAIRecipeDetail({} as IAIRecipeDetails));
	};

	const handleSave = () => {
		dispatch(setSimulationCommonSaveDrawer(true));
	};

	const handleShare = () => {
		dispatch(setSimulationCommonShareDrawer(true));
	};

	const handleSelectedDocumentChange = (selectedDocument: string[]) => {
		setSelectedDocument(selectedDocument);
	};
	const fetchPromptObligationResponse = async (event: any) => {
		setIsLoading(true);
		const promptKey = event.key;
		const responseFromApi = await generateAISimulationPromptResult(
			promptKey,
			citationInput,
			true,
		);
		if (responseFromApi?.responseBody && responseFromApi?.statusCode === OK) {
			setObligation(responseFromApi?.responseBody.answer);
			setIsLoading(false);
		} else {
			const { errorBody } = responseFromApi;
			const errorMsg = errorBody?.errorMessage as string;
			dispatch(
				setToastMessage({
					message: errorMsg || APP_GENERIC_BAD_REQUEST_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
			setIsLoading(false);
		}
	};

	const fetchObligationResponse = async () => {
		setIsLoading(true);
		setAnalysisResult([]);
		const responseFromApi = await generateAISimulationResult(
			OBLIGATION_GENERATION_KEY,
			citationInput,
			REQ_INV,
			true,
		);
		if (responseFromApi?.responseBody && responseFromApi?.statusCode === OK) {
			setObligation(responseFromApi?.responseBody.answer);
			setIsLoading(false);
		} else {
			const { errorBody } = responseFromApi;
			const errorMsg = errorBody?.errorMessage as string;
			if (responseFromApi?.statusCode === BAD_REQUEST) {
				dispatch(
					setToastMessage({
						message: errorMsg || APP_GENERIC_BAD_REQUEST_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			} else if (responseFromApi?.statusCode === NOT_FOUND) {
				dispatch(
					setToastMessage({
						message: errorMsg || APP_GENERIC_DATA_NOT_FOUND_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			} else {
				dispatch(
					setToastMessage({
						message: errorMsg || APP_GENERAL_ERROR_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			}
			setIsLoading(false);
		}
	};
	const handleStopGenerate = () => {
		setLoadNextResult(false);
	};
	/// Call api for each obligation
	const getGetAnalysisData = async (obligation: string, promptKey: string) => {
		const returnData = {
			sources: [],
			analysis: [] as ResultAnalysis[],
			obligation,
			errorMessage: '',
		};

		const resp = promptKey.length
			? await genPromptRegAnalysis(promptKey, selectedDocument, obligation)
			: await genRegAnalysis(selectedDocument, obligation);
		if (resp?.responseBody && resp?.statusCode === OK) {
			let excerptsAnswerApi;
			try {
				const res = resp?.responseBody.answer;
				excerptsAnswerApi = JSON.parse(res);
			} catch (ex) {
				excerptsAnswerApi = '';
			}
			if (excerptsAnswerApi.excerpts) {
				returnData.analysis = excerptsAnswerApi.excerpts;
			}
			if (resp?.responseBody.sources) {
				returnData.sources = resp?.responseBody.sources;
			}
		} else {
			const { errorBody } = resp;
			const errMsg = errorBody?.errorMessage as string;
			returnData.errorMessage = errMsg;
		}
		return returnData;
	};

	const removeFeedbackForObligationToPolicy = () => {
		const newFeedbackArray: IWorkFlowFeedback[] = [];

		workflowFeedback.forEach((ele) => {
			if (ele.workflowStep !== mapTitleWithModel[OBLIGATION_TO_POLICY]) {
				newFeedbackArray.push(ele);
			}
		});

		if (workflowFeedback.length !== newFeedbackArray.length) {
			dispatch(setWorkflowFeedback([...newFeedbackArray]));
		}
	};
	const fetchPromptAnalysisResponse = (e: any) => {
		const promptKey = e.key;
		updateLoadNextResult();
		getAnalysisResponse(promptKey);
	};

	const updateLoadNextResult = () => {
		if (!loadNextResult) {
			flushSync(() => {
				setLoadNextResult(true);
			});
		}
	};
	const fetchAnalysisResponse = () => {
		updateLoadNextResult();
		getAnalysisResponse('');
	};
	const getAnalysisResponse = async (promptKey: string) => {
		setIsLoadingResult(true);
		const obligationList = obligation.split('\n');
		setAnalysisResult([]);
		try {
			/// collect all api response for multiple obligation
			if (obligationToPolicyCommentList) {
				dispatch(setObligationToPolicyCommentList([]));
			}
			if (!selectedAIRecipe.id) {
				removeFeedbackForObligationToPolicy();
			}
			if (selectedAIRecipe.id) {
				dispatch(setObligtionToPolicyRegenerate(true));
			}
			await callObligationAnalysis(obligationList, promptKey);
			updateLoadNextResult();
			setIsLoadingResult(false);
		} catch {
			setIsLoadingResult(false);
			displayMatchError();
		}
		return null;
	};

	const callObligationAnalysis = async (
		obligationList: string[],
		promptKey: string,
	) => {
		/* eslint-disable no-restricted-syntax */
		setEnableBtn(false);
		for await (const obligationText of obligationList) {
			if (!loadNextResultRef.current) {
				break;
			}
			if (obligationText.length > 0) {
				const response = await getGetAnalysisData(obligationText, promptKey);
				if (response?.obligation && response.analysis.length) {
					const result = {
						...response,
						analysis: response.analysis.map((item) => {
							if (!item.id) {
								return {
									...item,
									id: uuidv4(),
								};
							}
							return { ...item };
						}),
					};
					flushSync(() => {
						setAnalysisResult((prev) => [...prev, result]);
					});
				}
			}
		}
		setEnableBtn(true);
	};

	const postCommentsApi = async (comment: string, paragraphId: string) => {
		if (selectedAIRecipe.id && !obligtionToPolicyRegenerate) {
			const postData: IObligationToPolicyComment = {
				simulationId: selectedAIRecipe.id,
				simulationName: selectedAIRecipe.name,
				type: RECIPE,
				comments: [
					{
						comment,
						paragraphId,
					},
				],
			};
			handlePostObligationComments(postData);
		} else {
			const newComment: IObligationComments = {
				id: '',
				email: loggedInUser?.email || '',
				comment,
				fullName: `${loggedInUser?.firstName} ${loggedInUser?.lastName}`,
				createdDate: new Date().toString(),
				simulationDetailId: '',
				paragraphId,
			};
			dispatch(
				setObligationToPolicyCommentList([
					...obligationToPolicyCommentList,
					newComment,
				]),
			);
		}
	};

	const getObligationToPolicyComments = async (simulationId: any) => {
		try {
			const resp = await getObligationComments(simulationId);
			if (resp && resp.responseBody) {
				const data = resp.responseBody as IObligationComments[];
				dispatch(setObligationToPolicyCommentList(data));
			}
		} catch (error) {
			dispatch(
				setToastMessage({
					message: 'Error fetching obligation comments',
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const deleteButtonClicked = async (simulationId: any) => {
		await deleteObligationComments(simulationId);
	};

	const displayMatchError = (errorMessage?: string) => {
		dispatch(
			setToastMessage({
				message: errorMessage || APP_GENERAL_ERROR_MSG,
				severity: 'error',
				isToastOpen: true,
			}),
		);
	};

	const backButtonHandler = () => {
		dispatch(setWorkflowFeedback([]));
		navigateToRegulationControl(navigate, regulationText);
	};

	const handleDownloadPDF = async () => {
		generateResultPDF(analysisResult);
		handleClose();
	};
	const handleDownloadExcel = async () => {
		generateResultExcel(analysisResult);
		handleClose();
	};

	return (
		<Stack>
			<StepHeader title="Obligation to policy" />
			{selectedRecipe && (
				<Stack
					direction="row"
					className={styles['btn-back-navigation']}
					onClick={backButtonHandler}
				>
					<BackIcon />
					<Typography className={styles['back-button-text']}>
						Back to Search Database
					</Typography>
				</Stack>
			)}
			<Grid container spacing={2} className={`${styles['card-container']}`}>
				<Grid item xs={4}>
					<Card className={styles.card} sx={scrollProps('y-axis')}>
						{!inputType && (
							<Box sx={{ padding: '0px 10px' }}>
								<Grid display="flex" gap="10px" flexDirection="column">
									<Typography className={styles['input-option-text']}>
										{toSentenceCase('Input Options')}
									</Typography>
									<ButtonComp
										key="upload-file-btn"
										btnType="primary"
										clickHandler={() => {}}
										data-testid="upload-file-btn"
										disabled
									>
										{toSentenceCase('Upload Regulation File')}
									</ButtonComp>
									<ButtonComp
										key="copy-paste-btn"
										btnType="primary"
										clickHandler={() => {
											setInputType('text');
										}}
										data-testid="copy-paste-btn"
									>
										{toSentenceCase('Copy/Paste')}
									</ButtonComp>
								</Grid>
							</Box>
						)}
						{inputType === 'text' && (
							<Stack
								direction="column"
								justifyContent="space-around"
								alignItems="flex-end"
								spacing={2}
								sx={{ width: '100%' }}
							>
								<ObligationPolicyInput
									citationInput={citationInput}
									setCitationInput={setCitationInput}
									obligation={obligation}
									genObligation={fetchObligationResponse}
									genPromptObligation={fetchPromptObligationResponse}
									setObligation={setObligation}
									genAnalysis={fetchAnalysisResponse}
									genPromptAnalysis={fetchPromptAnalysisResponse}
									isLoading={isLoading}
									isLoadingResult={isLoadingResult}
									selectedDocument={selectedDocument}
									onSelectedDocumentChange={handleSelectedDocumentChange}
									isShared={!!selectedAIRecipe.isShared}
								/>
							</Stack>
						)}
						{analysisResult.length > 0 && isLoadingResult === false && (
							<Stack
								direction="row"
								justifyContent="space-around"
								spacing={2}
								className={styles['button-group']}
								sx={{
									marginY: '20px',
									paddingX: '10px',
									width: '100%',
								}}
							>
								<ButtonComp
									key="restart-btn"
									btnType="tertiary"
									clickHandler={handleRestart}
									data-testid="restart-btn"
								>
									Restart
								</ButtonComp>
								<ButtonComp
									key="save-btn"
									btnType="tertiary"
									clickHandler={handleSave}
									data-testid="save-btn-Obligation"
								>
									Save
								</ButtonComp>
								<ButtonComp
									key="share-btn"
									btnType="tertiary"
									clickHandler={handleShare}
									data-testid="share-btn"
								>
									Share
								</ButtonComp>
							</Stack>
						)}
					</Card>
				</Grid>
				<Grid item xs={8} data-testid="result-card">
					<Card className={styles['result-card']}>
						<Stack direction="row" justifyContent="space-between">
							<Typography className={styles['card-result']}>Results</Typography>
							{analysisResult.length > 0 && (
								<>
									<ButtonComp
										btnType="secondary"
										variant="outlined"
										color="primary"
										aria-haspopup="true"
										compClass={`${
											isExportButtonEnabled && styles['download-disabled']
										}`}
										disabled={isExportButtonEnabled || !enableBtn}
										clickHandler={handleClick}
										data-testid="download-btn"
										sx={{
											fontSize: '16px',
											textTransform: 'none',
											borderRadius: 1,
											padding: '0px 25px',
											height: '32px',
											color: downloadBtnTextColor,
											backgroundColor: downloadBtnBgColor,
											borderColor: primaryBlue,
										}}
									>
										Export Results
										<DownArrowDropdown
											className={`${styles['export-icon']} ${
												open ? styles.up : ''
											}`}
											fill={primaryBlue}
										/>
									</ButtonComp>
									<Menu
										id="demo-positioned-menu"
										aria-labelledby="demo-positioned-button"
										anchorEl={anchorEl}
										open={open}
										onClose={handleClose}
										sx={{
											top: '32px',
										}}
										anchorOrigin={{
											vertical: 'top',
											horizontal: 'left',
										}}
										transformOrigin={{
											vertical: 'top',
											horizontal: 'left',
										}}
									>
										<MenuItem
											sx={{ width: '180px' }}
											onClick={handleDownloadExcel}
											data-testid="download-excel"
										>
											Excel
										</MenuItem>
										<MenuItem
											data-testid="download-pdf"
											onClick={handleDownloadPDF}
										>
											PDF
										</MenuItem>
									</Menu>
								</>
							)}
						</Stack>
						<Box
							className={styles['card-result-details']}
							sx={scrollProps('y-axis')}
						>
							{analysisResult.length === 0 && isLoadingResult === false ? (
								<>
									<div
										className={styles['image-center']}
										data-testid="inprogress-btn"
									>
										<img src={comingSoon} alt="doc" />
									</div>
									<div>
										<Typography className={styles['image-text']}>
											{toSentenceCase(OBLIGATION_TO_POLICY)}
										</Typography>
									</div>
								</>
							) : (
								<>
									{analysisResult.map((ele) => (
										<div key={uuidv4()}>
											<ResultCard
												key={uuidv4()}
												obligationOutput={ele}
												saveComment={postCommentsApi}
											/>
										</div>
									))}
									{isLoadingResult && (
										<Box className={styles['analysis-loader']}>
											<Stack
												direction="row"
												justifyContent="space-between"
												spacing={1}
											>
												<Loader show />
												<ButtonComp
													key="stop-btn"
													btnType="tertiary"
													clickHandler={handleStopGenerate}
													data-testid="stop-generate-btn"
													disabled={!loadNextResult}
												>
													{loadNextResult
														? OBLIGATION_STOP_GENERATING
														: `${OBLIGATION_STOPPING}...`}
												</ButtonComp>
											</Stack>
										</Box>
									)}
									<Stack
										direction="row"
										justifyContent="space-between"
										className={styles['ai-msg-wrapper']}
									>
										<Typography className={styles['disclaimer-text']}>
											{DISCLAIMER}
										</Typography>
										{isLoadingResult === false && (
											<AIFeedback
												objectType={mapTitleWithModel[OBLIGATION_TO_POLICY]}
												module={AI_SIMULATION_AND_EXPERIMENTATION}
												objectData={{
													prompt: regulationText,
													response: JSON.stringify({
														analysisResult,
													}),
													reference: selectedAIRecipe?.id || '',
												}}
												shouldResetFeedback={obligtionToPolicyRegenerate}
												allowSaveObjectType
											/>
										)}
									</Stack>
								</>
							)}
						</Box>
					</Card>
				</Grid>
			</Grid>
			<AIRecipeSave
				aiRecipeData={aiRecipeData}
				isSaveBtnVisible={false}
				isSaveBtnDisable={false}
				saveSimulationComment={saveSimulationComment}
			/>
			<AIShare
				aiSharedData={aiRecipeData}
				type={RECIPE}
				saveSimulationComment={saveSimulationComment}
			/>
		</Stack>
	);
};

export default ObligationToPolicy;
